/**
 * Lenavio Score badge + breakdown modal.
 *
 * Two visual states:
 *
 *   * Unscored  — "Score" pill with a pulse icon. Clicking it
 *                 fires a single-application scoring call.
 *   * Scored    — color-coded numeric pill (green ≥70, yellow
 *                 40–69, red <40). Clicking it opens the
 *                 breakdown modal with skills / experience /
 *                 culture signals / red flags / summary.
 *
 * Used inside the recruiting pipeline kanban + the candidates
 * list. The component owns its own modal state so the parent
 * doesn't have to plumb anything beyond ``application`` +
 * ``onScored`` (called after a successful single-shot score so
 * the parent can refresh its row).
 */
import { useState } from "react";
import { AlertTriangle, Sparkles, X } from "lucide-react";

import {
  scoreApplication,
  type AIScoreBreakdown,
  type Application,
} from "@/api/recruiting";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/hooks/useToast";

interface Props {
  application: Application;
  /** Called with the freshly-scored application after a manual
      single-shot score click. Parent uses it to refresh its row. */
  onScored?: (next: Application) => void;
  /** Compact mode for tight kanban cards — just shows the number. */
  compact?: boolean;
}

export function AIScoreBadge({ application, onScored, compact = false }: Props) {
  const { addToast } = useToast();
  const [scoring, setScoring] = useState(false);
  const [open, setOpen] = useState(false);

  const score = application.ai_score;
  const breakdown = application.ai_score_breakdown;
  const isScored = score !== null && score !== undefined;
  const hadError = Boolean(breakdown?.error);

  // Color buckets per spec: green ≥70, yellow 40-69, red <40.
  const tone = isScored
    ? hadError
      ? "neutral"
      : (score as number) >= 70
        ? "good"
        : (score as number) >= 40
          ? "warn"
          : "bad"
    : "neutral";

  const colors: Record<string, string> = {
    good: "bg-success/10 border-success/40 text-success",
    warn: "bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-400",
    bad: "bg-danger/10 border-danger/40 text-danger",
    neutral: "bg-border/30 border-border text-text-muted",
  };

  const handleScoreClick = async () => {
    if (isScored) {
      setOpen(true);
      return;
    }
    setScoring(true);
    try {
      const next = await scoreApplication(application.id);
      addToast("success", `Scored: ${next.ai_score}/100`);
      onScored?.(next);
      setOpen(true);
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not score this candidate.";
      addToast("error", detail);
    } finally {
      setScoring(false);
    }
  };

  const buttonClass = `inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition-opacity ${colors[tone]} ${
    scoring ? "opacity-60 cursor-wait" : "hover:opacity-90"
  }`;

  return (
    <>
      <button
        type="button"
        onClick={handleScoreClick}
        disabled={scoring}
        className={buttonClass}
        title={
          isScored
            ? "AI score — click for breakdown"
            : "Run AI scoring on this candidate"
        }
      >
        {isScored ? (
          <>
            {hadError ? (
              <AlertTriangle className="h-3 w-3" aria-hidden />
            ) : (
              <Sparkles className="h-3 w-3" aria-hidden />
            )}
            <span>{compact ? score : `${score}/100`}</span>
          </>
        ) : (
          <>
            <Sparkles className="h-3 w-3" aria-hidden />
            <span>{scoring ? "Scoring…" : "Score"}</span>
          </>
        )}
      </button>

      {isScored && (
        <BreakdownModal
          isOpen={open}
          onClose={() => setOpen(false)}
          score={score as number}
          breakdown={breakdown}
          scoredAt={application.ai_scored_at}
          model={application.ai_score_model}
          candidateName={
            application.candidate
              ? `${application.candidate.first_name} ${application.candidate.last_name}`
              : "Candidate"
          }
        />
      )}
    </>
  );
}


function BreakdownModal({
  isOpen,
  onClose,
  score,
  breakdown,
  scoredAt,
  model,
  candidateName,
}: {
  isOpen: boolean;
  onClose: () => void;
  score: number;
  breakdown: AIScoreBreakdown | null | undefined;
  scoredAt: string | null | undefined;
  model: string | null | undefined;
  candidateName: string;
}) {
  if (!breakdown) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="AI Score">
        <p className="text-sm text-text-muted">
          Score is set but no breakdown is on file. Re-run scoring from the
          candidate row to refresh.
        </p>
      </Modal>
    );
  }
  const tone =
    score >= 70 ? "text-success" : score >= 40 ? "text-amber-500" : "text-danger";
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`AI Score — ${candidateName}`}
      size="lg"
    >
      <div className="space-y-4">
        {breakdown.error && (
          <div className="rounded-lg border border-danger/40 bg-danger/5 px-3 py-2 text-sm text-danger flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
            <div>
              <p className="font-medium">Scoring didn't complete cleanly.</p>
              <p className="text-xs">{breakdown.error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Metric label="Overall" value={`${score}/100`} tone={tone} />
          <Metric
            label="Skills match"
            value={`${breakdown.skills_match}/100`}
          />
          <Metric
            label="Experience match"
            value={`${breakdown.experience_match}/100`}
          />
        </div>

        {breakdown.summary && (
          <section>
            <h4 className="text-xs uppercase tracking-wide text-text-muted mb-1">
              Summary
            </h4>
            <p className="text-sm text-text-primary">{breakdown.summary}</p>
          </section>
        )}

        {breakdown.culture_signals.length > 0 && (
          <section>
            <h4 className="text-xs uppercase tracking-wide text-text-muted mb-1">
              Positive signals
            </h4>
            <ul className="list-disc list-inside text-sm text-text-primary space-y-0.5">
              {breakdown.culture_signals.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </section>
        )}

        {breakdown.red_flags.length > 0 && (
          <section>
            <h4 className="text-xs uppercase tracking-wide text-text-muted mb-1">
              Concerns
            </h4>
            <ul className="list-disc list-inside text-sm text-danger space-y-0.5">
              {breakdown.red_flags.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </section>
        )}

        <p className="text-[11px] text-text-muted pt-1">
          {scoredAt
            ? `Scored ${new Date(scoredAt).toLocaleString()}`
            : "Recently scored"}
          {model ? ` · ${model}` : ""}
        </p>

        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-text-primary"
          >
            <X className="h-4 w-4" />
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}


function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <p className="text-xs uppercase tracking-wide text-text-muted">{label}</p>
      <p
        className={`text-2xl font-semibold font-heading ${tone || "text-text-primary"}`}
      >
        {value}
      </p>
    </div>
  );
}
